import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { ApiError, auth } from "../api/client";
import type { LoginResponse } from "../api/types";
import { useSession } from "../auth/session";
import { QrImage } from "../components/QrImage";
import { ErrorBox, Field, Wordmark, errorMessage } from "../components/ui";
import { homeFor } from "../App";
import { t } from "../i18n";
import { isValidEmail } from "../lib/validators";

type Step = { kind: "credentials" } | { kind: "totp"; login: LoginResponse };

export function Entrar() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  async function copySecret(secret: string) {
    try { await navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { /* sem clipboard (http): o segredo continua selecionável na tela */ }
  }
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function authError(e: unknown): string {
    if (e instanceof ApiError) {
      if (e.status === 429) return t.auth.rateLimited;
      if (e.code === "account_pending") return t.auth.accountPending;
      if (e.code === "account_rejected") return t.auth.accountRejected;
      if (e.status === 401 || e.code === "invalid_credentials") return t.auth.invalidCredentials;
      if (e.code === "invalid_code" || e.code === "totp_invalid" || (e.status === 400 && step.kind === "totp")) return t.auth.totpInvalid;
    }
    return errorMessage(e);
  }

  async function submitCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email || !password) return setError(t.auth.fillAll);
    if (!isValidEmail(email)) return setError(t.auth.invalidEmail);
    setPending(true);
    try {
      const r = await auth.login({ email: email.trim(), password });
      setPassword(""); // a senha não fica viva no estado durante a etapa do código
      setStep({ kind: "totp", login: r });
      setCode("");
    } catch (err) {
      setError(authError(err));
    } finally {
      setPending(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (step.kind !== "totp") return;
    setError(null);
    const c = code.replace(/\D/g, "");
    if (c.length !== 6) return setError(t.auth.totpInvalid);
    setPending(true);
    try {
      const body = { pendingToken: step.login.pendingToken, code: c };
      if (step.login.step === "totp_enroll") await auth.totpEnroll(body);
      else await auth.totpVerify(body);
      const me = await refresh();
      navigate(me ? homeFor(me) : "/", { replace: true });
    } catch (err) {
      setError(authError(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="card auth-card">
        <Wordmark style={{ fontSize: 28, paddingBottom: 12 }} />
        {step.kind === "credentials" ? (
          <form onSubmit={submitCredentials} className="stack" noValidate>
            <div className="stack s8">
              <h1 className="lead">{t.auth.signInLead}</h1>
              <p className="ink2">{t.auth.signInTitle}</p>
            </div>
            <Field label={t.auth.email}>
              {(id, d) => (
                <div className="inp">
                  <input id={id} type="email" autoComplete="username" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-describedby={d} required />
                </div>
              )}
            </Field>
            <Field label={t.auth.password}>
              {(id, d) => (
                <div className="inp">
                  <input id={id} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-describedby={d} required />
                </div>
              )}
            </Field>
            {error && <ErrorBox error={new Error(error)} />}
            <button className="btn full" type="submit" disabled={pending}>
              {pending ? t.auth.signingIn : t.auth.signIn}
            </button>
            <p className="ink2" style={{ fontSize: 13 }}>
              {t.auth.noAccount} <Link to="/criar-conta">{t.auth.createAccount}</Link> · <Link to="/esqueci-senha">{t.auth.forgotPassword}</Link>
            </p>
            <p className="ink2" style={{ fontSize: 12 }}>
              <Link to="/termos">{t.legal.terms}</Link> · <Link to="/privacidade">{t.legal.privacy}</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={submitCode} className="stack" noValidate>
            <div className="stack s8">
              <h1 className="lead" style={{ fontSize: 20 }}>{step.login.step === "totp_enroll" ? t.auth.totpEnrollTitle : t.auth.totpTitle}</h1>
              <p className="ink2">{step.login.step === "totp_enroll" ? t.auth.totpEnrollLead : t.auth.totpLead}</p>
            </div>
            {step.login.step === "totp_enroll" && step.login.otpauthUrl && (
              <div className="stack s8" style={{ alignItems: "center" }}>
                <QrImage value={step.login.otpauthUrl} alt="QR para o aplicativo autenticador" size={180} />
                {step.login.secret && (
                  <div className="stack s8" style={{ alignItems: "center", width: "100%" }}>
                    <span className="lbl">{t.auth.totpSecret}</span>
                    <code className="mono" style={{ fontSize: 13, overflowWrap: "anywhere", textAlign: "center", userSelect: "all" }}>{step.login.secret}</code>
                    <div className="row s8" style={{ justifyContent: "center", flexWrap: "wrap" }}>
                      <a className="btn" href={step.login.otpauthUrl} rel="noreferrer">{t.auth.totpOpenApp}</a>
                      <button type="button" className="btn ghost" onClick={() => void copySecret(step.login.secret!)}>{copied ? t.auth.totpCopied : t.auth.totpCopy}</button>
                    </div>
                    <p className="ink2" style={{ fontSize: 13, textAlign: "center" }}>{t.auth.totpSamePhone}</p>
                  </div>
                )}
              </div>
            )}
            <Field label={t.auth.totpCode}>
              {(id, d) => (
                <div className="inp">
                  <input id={id} className="mono" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} aria-describedby={d} autoFocus />
                </div>
              )}
            </Field>
            {error && <ErrorBox error={new Error(error)} />}
            <button className="btn full" type="submit" disabled={pending}>
              {pending ? t.auth.totpVerifying : t.auth.totpVerify}
            </button>
            <button type="button" className="linkbtn muted" onClick={() => { setStep({ kind: "credentials" }); setError(null); }}>
              {t.common.back}
            </button>
            {step.login.step === "totp_verify" && (
              <p className="ink2" style={{ fontSize: 13 }}>
                <Link to="/recuperar-2fa">{t.auth.lostAuthenticator}</Link>
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
